import { Badge, Box, Group, Stack, Text } from '@mantine/core'
import {
  IconArrowRampRight, IconBan, IconCode, IconShieldLock,
} from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { EstadoDeConsulta } from '@/components/ui'
import { acoesGatesQuery, rolesApi, type AcaoGate, type RoleGate } from '@/api/roles'

/**
 * Mostra onde uma funcao decide acesso, a partir do registro derivado do codigo.
 *
 * Somente leitura, e isso e a decisao e nao uma etapa: funcao<->permissao nao tem eixo
 * (`role_action_permissions` foi dropada na MIGRATION_048, e os endpoints de escrita
 * foram removidos na TASK-9329). Um controle editavel aqui prometeria conceder sem
 * conceder.
 *
 * O que este painel substitui e um `Alert` de texto livre que listava tres gates a mao:
 * ele nasceu correto e envelheceu quando a TASK-10809 acrescentou o eixo `dominio` ao
 * catalogo de ferramentas do @Atendente -- e o gate MAIOR passou a ser o que a tela nao
 * mencionava. Aqui nenhuma quantidade e escrita: `snapshot()` conta no servidor, na hora.
 */

/** Quadro de um gate. A moldura e a mesma dos itens da tela de Permissoes. */
function QuadroDeGate({ gate }: { gate: RoleGate }) {
  const q = gate.quantidade
  return (
    <Box
      style={{
        padding: '10px 12px',
        borderRadius: 4,
        border: '1px solid var(--ai-border-light)',
        backgroundColor: 'var(--ai-body-bg)',
      }}
    >
      <Group gap={8} align="flex-start" wrap="nowrap" mb={4}>
        <IconShieldLock size={15} style={{ color: 'var(--ai-text-secondary)', flexShrink: 0, marginTop: 2 }} />
        <Text fw={700} size="xs" c="var(--ai-text-primary)" style={{ flex: 1 }}>
          {gate.superficie}
        </Text>
        {q?.valor != null && q.de != null && (
          <Badge size="sm" variant="light" color="inovareTeal" ff="monospace" tt="none">
            {q.valor} de {q.de}
          </Badge>
        )}
      </Group>

      <Text size="xs" c="var(--ai-text-primary)">{gate.efeito}</Text>

      {q?.ainda_alcancaveis != null && (
        <Group gap={6} mt={6} wrap="nowrap" align="flex-start">
          <IconArrowRampRight size={14} style={{ color: 'var(--ai-text-secondary)', flexShrink: 0, marginTop: 1 }} />
          <Text size="xs" c="var(--ai-text-secondary)">
            <Text span ff="monospace" fw={700}>{q.ainda_alcancaveis}</Text>
            {' '}dessas seguem alcançáveis sem a função, pela porta única.
          </Text>
        </Group>
      )}

      {gate.compensado_por && (
        <Text size="xs" c="var(--ai-text-secondary)" mt={6} ml={20}>
          {gate.compensado_por}
        </Text>
      )}

      {gate.nao_concede.length > 0 && (
        <Group gap={6} mt={6} wrap="nowrap">
          <IconBan size={14} style={{ color: 'var(--mantine-color-red-6)', flexShrink: 0 }} />
          <Text size="xs" c="var(--ai-text-secondary)">
            Não abre este gate:{' '}
            {gate.nao_concede.map((n) => (
              <Text span key={n} ff="monospace" fw={700}>{n}</Text>
            ))}
          </Text>
        </Group>
      )}

      <Group gap={6} mt={6} wrap="nowrap">
        <IconCode size={13} style={{ color: 'var(--ai-text-muted)', flexShrink: 0 }} />
        <Text size="xs" c="var(--ai-text-secondary)" ff="monospace" style={{ wordBreak: 'break-all' }}>
          {gate.onde}
        </Text>
      </Group>
    </Box>
  )
}

/** Uma acao do chat que exige ESTA funcao, com o que mais e cobrado depois dela. */
function LinhaDeAcao({ acao }: { acao: AcaoGate }) {
  return (
    <Box
      style={{
        padding: '8px 10px',
        borderRadius: 4,
        border: '1px solid var(--ai-border-light)',
        backgroundColor: 'var(--ai-body-bg)',
      }}
    >
      <Group gap={8} wrap="nowrap" align="center">
        <Text size="xs" fw={600} c="var(--ai-text-primary)" style={{ flex: 1 }}>
          {acao.label}
        </Text>
        {acao.permissao ? (
          <Badge size="xs" variant="light" color="inovareTeal" ff="monospace" tt="none">
            {acao.permissao}
          </Badge>
        ) : (
          <Badge size="xs" variant="light" color="red" tt="none">
            sem permissão a conceder
          </Badge>
        )}
      </Group>
      <Text size="xs" c="var(--ai-text-primary)" mt={2}>{acao.motivo}</Text>
      {acao.caminho_alternativo && (
        <Text size="xs" c="var(--ai-text-secondary)" mt={2}>{acao.caminho_alternativo}</Text>
      )}
    </Box>
  )
}

export function RoleGatesPanel({ roleCode }: { roleCode: string }) {
  const gatesQuery = useQuery({
    queryKey: ['role-gates', roleCode],
    queryFn: () => rolesApi.getRoleGates(roleCode),
    enabled: !!roleCode,
  })
  const acoesQuery = useQuery(acoesGatesQuery)

  const gates = gatesQuery.data?.gates ?? []
  const acoesDaFuncao = (acoesQuery.data?.acoes ?? []).filter(
    (a) => a.funcoes_exigidas.includes(roleCode),
  )

  return (
    <Stack gap="md">
      <Text size="xs" c="var(--ai-text-secondary)">
        Derivado do código em execução, não de cadastro. Função não concede permissão:
        quem concede é o <b>Perfil</b>, em <b>Admin &rarr; Permissões</b>. O que a função
        faz é abrir os pontos abaixo.
      </Text>

      <EstadoDeConsulta
        consulta={gatesQuery}
        vazio={gates.length === 0}
        estadoVazio={
          <Box
            style={{
              padding: '12px 14px',
              borderRadius: 4,
              border: '1px solid var(--ai-border-light)',
              backgroundColor: 'var(--ai-body-bg)',
            }}
          >
            <Text size="xs" fw={600} c="var(--ai-text-primary)" mb={2}>
              Esta função não decide acesso em nenhum ponto do código
            </Text>
            <Text size="xs" c="var(--ai-text-primary)">
              Ela existe, pode ter usuários, e nenhum gate a consulta. Atribuir ou
              remover esta função de alguém não muda o acesso dele — o acesso vem do
              Perfil.
            </Text>
          </Box>
        }
      >
        <Stack gap="xs">
          {gates.map((g) => <QuadroDeGate key={g.onde + g.superficie} gate={g} />)}
        </Stack>
      </EstadoDeConsulta>

      {/*
        A falha desta consulta nao pode ficar muda: sem ela a secao simplesmente nao
        aparece, e "nao ha acao que exija esta funcao" e' uma afirmacao diferente de
        "nao deu para saber". E' o mesmo defeito que o painel de cima evita pelo
        `EstadoDeConsulta` -- aqui ele voltaria pela porta do `&&`.
      */}
      {acoesQuery.isError && (
        <Text size="xs" c="var(--mantine-color-red-6)">
          Não foi possível carregar as ações do chat desta função — isto não significa
          que não existam.
        </Text>
      )}

      {acoesQuery.isSuccess && acoesDaFuncao.length > 0 && (
        <Stack gap="xs">
          <Text fw={700} size="xs" c="var(--ai-text-primary)">
            Ações do chat que exigem esta função ({acoesDaFuncao.length})
          </Text>
          {acoesDaFuncao.map((a) => <LinhaDeAcao key={a.acao} acao={a} />)}
        </Stack>
      )}
    </Stack>
  )
}
